<template>
  <div class="p-4">
    <Loader v-if="$apollo.queries.brand.loading" label="Loading Brand..." />

    <Error
      v-if="!$apollo.queries.brand.loading && errors.length"
      :errors="errors"
      class="m-4"
    />

    <div class="items-wrap">
      <section v-if="!$apollo.queries.brand.loading" class="item brand-logo">
        <div class="flex items-center mb-4">
          <p class="title mb-0">
            Logo
          </p>

          <button
            class="change-image-toggle ml-auto p-0"
            @click="onChangeImage"
          >
            {{ changeImageToggle ? "Cancel" : "Change" }}
          </button>
        </div>

        <Uploader
          id="brandUploader"
          :key="logoImageUrl"
          :src="logoImageUrl"
          :edit-mode="changeImageToggle"
          @uploading-complete="onUploadComplete"
          @image-click="onChangeImage"
        />
      </section>

      <section v-if="!$apollo.queries.brand.loading" class="item brand-colors">
        <p class="title">
          Colors
        </p>

        <BrandColors
          :key="primaryColor || secondaryColor"
          :primary-color="primaryColor"
          :secondary-color="secondaryColor"
          :loading="updatingBrand"
          @color-selected="onColorSelected"
        />
      </section>

      <DevBrands
        v-if="DEVELOPMENT && !$apollo.queries.brand.loading"
        @brand-selected="onDevBrandSelected"
      />
    </div>
  </div>
</template>

<script>
import Uploader from "@/components/UI/Uploader";
import Loader from "@/components/UI/Loader";
import BrandColors from "./BrandColors";
import Error from "@/components/UI/Error";
import { Brand } from "@/graphql/queries";
import { UpdateBrand } from "@/graphql/mutations";
import EventBus from "@/lib/event-bus.js";

// Import the DevBrands and only show in development...
const DevBrands = () => ({
  // The component to load (should be a Promise)
  component: import(
    /* webpackChunkName: "Onboarding" */ "@/components/Brand/DevBrands"
  ),
  delay: 0,
  timeout: 10000,
});

export default {
  apollo: {
    $query: {
      fetchPolicy: "cache-first",
    },
    brand: {
      query: () => Brand,
      result({ loading, data }) {
        if (!loading) {
          this.logoImageUrl = data?.brand?.logoImageUrl;
          this.primaryColor = data?.brand?.primaryColor;
          this.secondaryColor = data?.brand?.secondaryColor;
        }
      },
      error(error) {
        this.errors.push(error);
      },
    },
  },
  name: "BrandEditor",
  inject: ["DEVELOPMENT"],
  components: { BrandColors, DevBrands, Error, Loader, Uploader },
  props: {
    campaignId: {
      required: true,
      type: String,
    },
  },
  data: () => ({
    errors: [],
    changeImageToggle: false,
    logoImageUrl: "",
    primaryColor: "",
    secondaryColor: "",
    updatingBrand: false,
  }),
  computed: {
    changeImage() {
      return (
        !this.$apollo.loading && this.logoImageUrl && this.changeImageToggle
      );
    },
  },
  methods: {
    onChangeImage() {
      this.changeImageToggle = !this.changeImageToggle;
    },
    onColorSelected(color) {
      this.brand[color.item] = color.value;
      this.updateBrandMutation();
    },
    onDevBrandSelected(brand) {
      this.logoImageUrl = brand?.logoImageUrl;
      this.primaryColor = brand?.primaryColor;
      this.secondaryColor = brand?.secondaryColor;

      // Wait for the dom to update the update the brand mutation...
      this.$nextTick(() => this.updateBrandMutation());
    },
    onUploadComplete(url) {
      this.logoImageUrl = url;
      this.onChangeImage();
      this.updateBrandMutation();
    },
    updateBrandMutation: async function() {
      this.updatingBrand = true;

      // Emit the preview loading event to put the Preview component in it's loading state...
      EventBus.$emit("preview-loading", true);

      const { logoImageUrl, primaryColor, secondaryColor } = this;

      const brandInput = {
        logoImageUrl,
        primaryColor,
        secondaryColor,
      };

      try {
        await this.$apollo.mutate({
          mutation: UpdateBrand,
          variables: {
            input: brandInput,
          },
          update: (store, { data: { updateBrand } }) => {
            this.updateBrand({
              store,
              response: updateBrand,
            });

            EventBus.$emit("refetch-preview");
          },
        });

        this.updatingBrand = false;
      } catch (error) {
        this.updatingBrand = false;
      }
    },
    updateBrand({ store, response }) {
      // Read from the store
      const data = store.readQuery({
        query: Brand,
        // variables: brandInput
      });

      // Update the cache with the response
      data.brand.logoImageUrl = response.logoImageUrl;
      data.brand.primaryColor = response.primaryColor;
      data.brand.secondaryColor = response.secondaryColor;

      // Write our data back to the cache.
      store.writeQuery({
        query: Brand,
        // variables: brandInput,
        data,
      });
    },
  },
};
</script>

<style scoped>
.item {
  @apply mb-8 w-full;

  @screen sm {
    @apply w-2/3;
  }

  @screen md {
    @apply w-1/2;
  }
}

.brand-logo {
  @apply max-w-xs;
}

.title {
  @apply mb-4 text-support text-sm font-bold;
}

.subtitle {
  @apply text-grey-dark mb-4 font-normal font-sans;
}

.change-image-toggle {
  @apply p-0 m-0 text-brand outline-none;
  font-size: 14px;
  line-height: 1;
  vertical-align: text-top;

  &:hover {
    @apply underline;
  }
}
</style>
